import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Description, Field, Label, Switch } from "@headlessui/react";
import { toast } from "sonner";
import { setOrderContractDeferred } from "services/orders.service";

type Props = {
  companyId: string;
  orderId: string;
  contractDeferred: boolean;
  /** Only a Master may change it (the server checks too); others just see it. */
  canChange: boolean;
  onChanged: () => void;
};

/**
 * "Contract will be signed later": lets the order be invoiced before its
 * contract is signed. Saved as soon as it is switched.
 */
export default function ContractDeferredSwitch({
  companyId,
  orderId,
  contractDeferred,
  canChange,
  onChanged,
}: Props) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);

  const handleChange = async (next: boolean) => {
    setSaving(true);
    try {
      await setOrderContractDeferred({ companyId, orderId, contractDeferred: next });
      onChanged();
    } catch {
      toast.error(
        t("contract_deferred_save_failed", {
          defaultValue: "Could not save. Please try again.",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Field className="flex items-start gap-3">
      <Switch
        checked={contractDeferred}
        onChange={handleChange}
        disabled={!canChange || saving}
        className={`${
          contractDeferred ? "bg-indigo-600" : "bg-zinc-200 dark:bg-zinc-700"
        } relative mt-0.5 inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50`}
      >
        <span
          className={`${
            contractDeferred ? "translate-x-5" : "translate-x-1"
          } inline-block size-4 rounded-full bg-white transition`}
        />
      </Switch>
      <div>
        <Label className="text-sm font-medium text-zinc-800 dark:text-zinc-100">
          {t("contract_deferred", {
            defaultValue: "Contract will be signed later",
          })}
        </Label>
        <Description className="mt-0.5 text-xs text-zinc-500">
          {t("contract_deferred_help", {
            defaultValue:
              "Invoices can be issued before the contract is signed. Once it is signed, this no longer matters.",
          })}
        </Description>
      </div>
    </Field>
  );
}
